import { useTranslation } from"react-i18next";
import { useState, useEffect, useCallback } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from"@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Label } from"@/components/ui/label";
import { Badge } from"@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from"@/components/ui/dialog";
import { ScrollArea } from"@/components/ui/scroll-area";
import { TrendingUp, TrendingDown, DollarSign, Calendar, ArrowUpCircle, ArrowDownCircle, ChevronRight, FileText, Home, Building, Clock, AlertCircle, ArrowRight } from"lucide-react";
import { toast } from"sonner";

// Utility functions
const formatCurrency = (amount) => {
 const absAmount = Math.abs(amount || 0);
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: 'EUR',
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(absAmount);
};

const formatDate = (dateStr) => {
 if (!dateStr) return"N/A";
 try {
 return new Date(dateStr).toLocaleDateString('en-US', {
 month: 'short',
 day: 'numeric',
 year: 'numeric'
 });
 } catch {
 return dateStr;
 }
};

const getCashPositionColor = (amount) => {
 if (amount > 0) return"text-green-700";
 if (amount < 0) return"text-red-700";
 return"text-[#334155]";
};

const getNetFlowColor = (amount) => {
 if (amount > 0) return"bg-green-50/80 border-green-200/60 text-green-900";
 if (amount < 0) return"bg-red-50/80 border-red-200/60 text-red-900";
 return"bg-white/50 border-white/60 text-[#0F172A]";
};

// Current Position Detail Modal Component
const CurrentPositionModal = ({ open, onClose, currentPosition }) => (
 <Dialog open={open} onOpenChange={onClose}>
 <DialogContent className="max-w-2xl max-h-[80vh]">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <DollarSign className="w-5 h-5 text-blue-600"/>
 Current Cash Position Details
 </DialogTitle>
 <DialogDescription>
 Breakdown of all cash inflows and outflows affecting your current position
 </DialogDescription>
 </DialogHeader>
 <ScrollArea className="h-[60vh] pr-4">
 {currentPosition && (
 <div className="space-y-6">
 {/* Summary Cards */}
 <div className="grid grid-cols-3 gap-4">
 <Card className="bg-blue-50/80 border-blue-200/60">
 <CardContent className="p-4 text-center">
 <p className="text-xs text-blue-600 font-medium">Cash on Hand</p>
 <p className={`text-lg font-bold ${currentPosition.cash_on_hand >= 0 ? 'text-green-700' : 'text-red-700'}`}>
 {currentPosition.cash_on_hand >= 0 ? '' : '-'}{formatCurrency(currentPosition.cash_on_hand)}
 </p>
 </CardContent>
 </Card>
 <Card className="bg-green-50/80 border-green-200/60">
 <CardContent className="p-4 text-center">
 <p className="text-xs text-green-600 font-medium">Total Received</p>
 <p className="text-lg font-bold text-green-700">{formatCurrency(currentPosition.total_received)}</p>
 </CardContent>
 </Card>
 <Card className="bg-red-50/80 border-red-200/60">
 <CardContent className="p-4 text-center">
 <p className="text-xs text-red-600 font-medium">Total Spent</p>
 <p className="text-lg font-bold text-red-700">{formatCurrency(currentPosition.total_spent)}</p>
 </CardContent>
 </Card>
 </div>

 {/* Transactions List */}
 <div>
 <h4 className="font-semibold text-[#334155] mb-3 flex items-center gap-2">
 <FileText className="w-4 h-4"/>
 Transaction History
 </h4>
 {currentPosition.transactions?.length > 0 ? (
 <div className="space-y-2">
 {currentPosition.transactions.map((tx, idx) => (
 <div key={idx} className={`p-3 rounded-lg border ${tx.type === 'income' ? 'bg-green-50/80 border-green-200/60' : 'bg-red-50/80 border-red-200/60'}`}>
 <div className="flex justify-between items-start">
 <div className="flex-1">
 <p className="font-medium text-sm text-slate-800">{tx.source}</p>
 {tx.buyer && <p className="text-[11px] text-[#94A3B8]">Buyer: {tx.buyer}</p>}
 {tx.company && <p className="text-[11px] text-[#94A3B8]">Vendor: {tx.company}</p>}
 {tx.category && <p className="text-[11px] text-[#94A3B8]">Category: {tx.category}</p>}
 </div>
 <div className="text-right">
 <p className={`font-bold ${tx.amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
 {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
 </p>
 <p className="text-xs text-slate-400">{formatDate(tx.date)}</p>
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-[#94A3B8] text-sm text-center py-4">No transactions recorded yet</p>
 )}
 </div>
 </div>
 )}
 </ScrollArea>
 </DialogContent>
 </Dialog>
);

// Forecast Period Detail Modal Component
const ForecastPeriodModal = ({ open, onClose, title, period, data }) => (
 <Dialog open={open} onOpenChange={onClose}>
 <DialogContent className="max-w-2xl max-h-[80vh]">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <Calendar className="w-5 h-5 text-blue-600"/>
 {title}
 </DialogTitle>
 <DialogDescription>
 Expected cash inflows and outflows for {period}
 </DialogDescription>
 </DialogHeader>
 <ScrollArea className="h-[60vh] pr-4">
 {data && (
 <div className="space-y-6">
 {/* Summary Cards */}
 <div className="grid grid-cols-2 gap-4">
 <Card className="bg-green-50/80 border-green-200/60">
 <CardContent className="p-4">
 <div className="flex items-center gap-2 mb-2">
 <TrendingUp className="w-4 h-4 text-green-600"/>
 <p className="text-xs text-green-600 font-medium">Expected Incoming</p>
 </div>
 <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency(data.incoming)}</p>
 </CardContent>
 </Card>
 <Card className="bg-red-50/80 border-red-200/60">
 <CardContent className="p-4">
 <div className="flex items-center gap-2 mb-2">
 <TrendingDown className="w-4 h-4 text-red-600"/>
 <p className="text-xs text-red-600 font-medium">Expected Outgoing</p>
 </div>
 <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency(data.outgoing)}</p>
 </CardContent>
 </Card>
 </div>

 <Card className="">
 <CardContent className="p-4 flex justify-between items-center">
 <div>
 <p className="text-xs text-[#475569] font-medium">Net Cash Flow</p>
 <p className={`text-xl font-bold ${data.net >= 0 ? 'text-green-700' : 'text-red-700'}`}>
 {data.net >= 0 ? '+' : ''}{formatCurrency(data.net)}
 </p>
 </div>
 <ArrowRight className="w-5 h-5 text-slate-400"/>
 <div className="text-right">
 <p className="text-xs text-[#475569] font-medium">Projected Position</p>
 <p className={`text-xl font-bold ${data.projected_position >= 0 ? 'text-green-700' : 'text-red-700'}`}>
 {formatCurrency(data.projected_position)}
 </p>
 </div>
 </CardContent>
 </Card>

 {/* Incoming Transactions */}
 <div>
 <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
 <TrendingUp className="w-4 h-4"/>
 Expected Incoming ({data.incoming_transactions?.length || 0})
 </h4>
 {data.incoming_transactions?.length > 0 ? (
 <div className="space-y-2">
 {data.incoming_transactions.map((tx, idx) => (
 <div key={idx} className="p-3 rounded-lg bg-green-50/80 border border-green-200/60">
 <div className="flex justify-between items-start">
 <div className="flex-1">
 <div className="flex items-center gap-2">
 {tx.type === 'sale_payment' && <Home className="w-4 h-4 text-green-600"/>}
 {tx.type === 'rental_income' && <Building className="w-4 h-4 text-green-600"/>}
 <p className="font-medium text-sm text-slate-800">
 {tx.type === 'sale_payment' ? `Sale Payment - Unit ${tx.unit_code}` : `Rental Income - Unit ${tx.unit_code}`}
 </p>
 </div>
 {tx.buyer_name && <p className="text-[11px] text-[#94A3B8] ml-6">Buyer: {tx.buyer_name}</p>}
 {tx.tenant_name && <p className="text-[11px] text-[#94A3B8] ml-6">Tenant: {tx.tenant_name}</p>}
 <p className="text-xs text-slate-400 ml-6">{tx.description}</p>
 </div>
 <div className="text-right">
 <p className="font-bold text-green-700">+{formatCurrency(tx.amount)}</p>
 <p className="text-xs text-slate-400">Due: {formatDate(tx.due_date)}</p>
 {tx.days_until_due !== undefined && (
 <Badge variant="outline"className="text-xs mt-1">
 {tx.days_until_due} days
 </Badge>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-[#94A3B8] text-sm text-center py-4 bg-white/50 rounded-lg">No expected incoming for this period</p>
 )}
 </div>

 {/* Outgoing Transactions */}
 <div>
 <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
 <TrendingDown className="w-4 h-4"/>
 Expected Outgoing ({data.outgoing_transactions?.length || 0})
 </h4>
 {data.outgoing_transactions?.length > 0 ? (
 <div className="space-y-2">
 {data.outgoing_transactions.map((tx, idx) => (
 <div key={idx} className="p-3 rounded-lg bg-red-50/80 border border-red-200/60">
 <div className="flex justify-between items-start">
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <FileText className="w-4 h-4 text-red-600"/>
 <p className="font-medium text-sm text-slate-800">Invoice #{tx.invoice_number}</p>
 </div>
 <p className="text-[11px] text-[#94A3B8] ml-6">Project: {tx.project_name}</p>
 {tx.description && <p className="text-xs text-slate-400 ml-6 truncate max-w-xs">{tx.description}</p>}
 </div>
 <div className="text-right">
 <p className="font-bold text-red-700">-{formatCurrency(tx.amount)}</p>
 <p className="text-xs text-slate-400">Due: {formatDate(tx.due_date)}</p>
 <Badge variant={tx.status === 'accepted' ? 'outline' : 'secondary'} className="text-xs mt-1">
 {tx.status}
 </Badge>
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-[#94A3B8] text-sm text-center py-4 bg-white/50 rounded-lg">No expected outgoing for this period</p>
 )}
 </div>
 </div>
 )}
 </ScrollArea>
 </DialogContent>
 </Dialog>
);

// Outstanding Invoices Modal Component
const OutstandingInvoicesModal = ({ open, onClose, outstandingInvoices }) => (
 <Dialog open={open} onOpenChange={onClose}>
 <DialogContent className="max-w-2xl max-h-[80vh]">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-orange-600"/>
 Outstanding Invoices
 </DialogTitle>
 <DialogDescription>
 All unpaid or partially paid invoices requiring attention
 </DialogDescription>
 </DialogHeader>
 <ScrollArea className="h-[60vh] pr-4">
 {outstandingInvoices && (
 <div className="space-y-6">
 {/* Summary */}
 <Card className="bg-orange-50/80 border-orange-200/60">
 <CardContent className="p-4 flex justify-between items-center">
 <div>
 <p className="text-xs text-orange-600 font-medium">Total Outstanding</p>
 <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency(outstandingInvoices.total)}</p>
 </div>
 <Badge variant="outline"className="text-orange-700 border-orange-300">
 {outstandingInvoices.count} invoice(s)
 </Badge>
 </CardContent>
 </Card>

 {/* Invoice List */}
 <div>
 <h4 className="font-semibold text-[#334155] mb-3 flex items-center gap-2">
 <FileText className="w-4 h-4"/>
 Invoice Details
 </h4>
 {outstandingInvoices.details?.length > 0 ? (
 <div className="space-y-2">
 {outstandingInvoices.details.map((inv, idx) => (
 <div key={idx} className="p-4 rounded-lg bg-white/70 backdrop-blur-sm border border-white/60">
 <div className="flex justify-between items-start mb-2">
 <div>
 <p className="font-semibold text-slate-800">Invoice #{inv.invoice_number}</p>
 <p className="text-sm text-[#94A3B8]">{inv.project_name}</p>
 </div>
 <Badge variant={
 inv.status === 'accepted' ? 'outline' : 
 inv.status === 'partially_paid' ? 'secondary' : 'default'
 }>
 {inv.status}
 </Badge>
 </div>
 
 <div className="grid grid-cols-3 gap-4 mt-3">
 <div>
 <p className="text-xs text-slate-400">Total Amount</p>
 <p className="font-medium text-[#334155]">{formatCurrency(inv.total_amount)}</p>
 </div>
 <div>
 <p className="text-xs text-slate-400">Paid</p>
 <p className="font-medium text-green-600">{formatCurrency(inv.total_paid)}</p>
 </div>
 <div>
 <p className="text-xs text-slate-400">Outstanding</p>
 <p className="font-bold text-orange-600">{formatCurrency(inv.amount)}</p>
 </div>
 </div>
 
 <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/40">
 <div className="flex items-center gap-1 text-[11px] text-[#94A3B8]">
 <Clock className="w-3 h-3"/>
 Due: {formatDate(inv.due_date)}
 </div>
 {inv.days_until_due !== undefined && (
 <Badge variant={inv.days_until_due < 0 ? 'destructive' : inv.days_until_due <= 7 ? 'secondary' : 'outline'} className="text-xs">
 {inv.days_until_due < 0 ? `${Math.abs(inv.days_until_due)} days overdue` : `${inv.days_until_due} days left`}
 </Badge>
 )}
 </div>
 
 {inv.description && (
 <p className="text-xs text-slate-400 mt-2 truncate">{inv.description}</p>
 )}
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-8 bg-green-50/80 rounded-lg border border-green-200/60">
 <p className="text-green-700 font-medium">No Outstanding Invoices</p>
 <p className="text-green-600 text-sm">All invoices are paid!</p>
 </div>
 )}
 </div>
 </div>
 )}
 </ScrollArea>
 </DialogContent>
 </Dialog>
);

// Main Component
const CashFlowPageV2 = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("all");
 const [forecast, setForecast] = useState(null);
 const [loading, setLoading] = useState(true);
 
 // Modal states
 const [showCurrentPositionModal, setShowCurrentPositionModal] = useState(false);
 const [showForecast30Modal, setShowForecast30Modal] = useState(false);
 const [showForecast60Modal, setShowForecast60Modal] = useState(false);
 const [showForecast90Modal, setShowForecast90Modal] = useState(false);
 const [showOutstandingInvoicesModal, setShowOutstandingInvoicesModal] = useState(false);

 const loadProjects = useCallback(async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 }, []);

 const loadForecast = useCallback(async () => {
 setLoading(true);
 try {
 const url = selectedProject ==="all"
 ?"/finance/cash-flow-details"
 : `/finance/cash-flow-details?project_id=${selectedProject}`;
 const response = await axios.get(url);
 setForecast(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 }, [selectedProject]);

 useEffect(() => {
 loadProjects();
 }, [loadProjects]);

 useEffect(() => {
 loadForecast();
 }, [loadForecast]);

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-[#0F172A]">
 Cash Flow Forecast
 </h1>
 <p className="text-[#94A3B8] mt-1">
 Click on any card to view detailed breakdown
 </p>
 </div>
 <div className="flex items-center gap-3">
 <Label>Project:</Label>
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All Projects</SelectItem>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>

 {loading ? (
 <div className="flex items-center justify-center h-64">
 <div className="text-[#475569]">Loading forecast...</div>
 </div>
 ) : forecast && forecast.current_position ? (
 <>
 {/* Current Position - Clickable Cards */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 <Card 
 className="bg-white/70 backdrop-blur-xl border border-white/60 rounded-[20px] cursor-pointer transition-all duration-300 hover:shadow-[0_20px_50px_rgb(0,0,0,0.12)] hover:-translate-y-1"
 onClick={() => setShowCurrentPositionModal(true)}
 data-testid="current-position-card"
 >
 <CardContent className="pt-6">
 <div className="flex items-center justify-between mb-2">
 <div className="flex items-center gap-3">
 <DollarSign className="w-5 h-5 text-blue-600"/>
 <p className="text-sm font-semibold text-blue-900">Current Cash Position</p>
 </div>
 <ChevronRight className="w-4 h-4 text-slate-400"/>
 </div>
 <p className={`text-3xl font-bold ${getCashPositionColor(forecast.current_position?.cash_on_hand)}`}>
 {(forecast.current_position?.cash_on_hand ?? 0) >= 0 ? '' : '-'}{formatCurrency(forecast.current_position?.cash_on_hand ?? 0)}
 </p>
 <div className="mt-3 text-xs text-[#475569] space-y-1">
 <p>Received: {formatCurrency(forecast.current_position?.total_received ?? 0)}</p>
 <p>Spent: {formatCurrency(forecast.current_position?.total_spent ?? 0)}</p>
 </div>
 </CardContent>
 </Card>

 <Card 
 className="cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
 onClick={() => setShowForecast30Modal(true)}
 data-testid="forecast-30-summary-card"
 >
 <CardContent className="pt-6">
 <div className="flex items-center justify-between mb-2">
 <div className="flex items-center gap-3">
 <ArrowUpCircle className="w-5 h-5 text-green-600"/>
 <p className="text-sm font-semibold text-[#334155]">Expected Revenue (30d)</p>
 </div>
 <ChevronRight className="w-4 h-4 text-slate-400"/>
 </div>
 <p className="text-2xl font-semibold tabular-nums text-[#0F172A]">
 {formatCurrency(forecast.forecast_30_days.incoming)}
 </p>
 <div className="mt-3 text-xs text-[#475569]">
 <p>Net: {forecast.forecast_30_days.net >= 0 ? '+' : ''}{formatCurrency(forecast.forecast_30_days.net)}</p>
 </div>
 </CardContent>
 </Card>

 <Card 
 className={`cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all ${
 (forecast.outstanding_invoices?.total || 0) > 0 ? 'border-2 border-orange-200/60' : ''
 }`}
 onClick={() => setShowOutstandingInvoicesModal(true)}
 data-testid="outstanding-invoices-card"
 >
 <CardContent className="pt-6">
 <div className="flex items-center justify-between mb-2">
 <div className="flex items-center gap-3">
 <ArrowDownCircle className={`w-5 h-5 ${(forecast.outstanding_invoices?.total || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`} />
 <p className="text-sm font-semibold text-[#334155]">Outstanding Invoices</p>
 </div>
 <ChevronRight className="w-4 h-4 text-slate-400"/>
 </div>
 <p className={`text-3xl font-bold ${(forecast.outstanding_invoices?.total || 0) > 0 ? 'text-orange-700' : 'text-green-700'}`}>
 {formatCurrency(forecast.outstanding_invoices?.total || forecast.summary.total_outstanding_invoices)}
 </p>
 <div className="mt-3 text-xs text-[#475569]">
 <p>{forecast.outstanding_invoices?.count || forecast.data_sources.pending_invoices_count} unpaid invoice(s)</p>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Forecast Periods - Clickable */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 {/* 30 Days */}
 <Card 
 className="border-green-200/60 hover:shadow-lg transition-all cursor-pointer"
 onClick={() => setShowForecast30Modal(true)}
 data-testid="forecast-30-card"
 >
 <CardHeader className="border-b border-white/40 flex flex-row items-center justify-between">
 <CardTitle className="flex items-center gap-2 text-[#0F172A]">
 <Calendar className="w-5 h-5"/>
 Next 30 Days
 </CardTitle>
 <Badge variant="outline"className="bg-emerald-500/10 text-emerald-700">
 {(forecast.forecast_30_days.incoming_transactions?.length || 0) + (forecast.forecast_30_days.outgoing_transactions?.length || 0)} items
 </Badge>
 </CardHeader>
 <CardContent className="p-6 space-y-4">
 <div className="space-y-3">
 <div className="flex justify-between items-center">
 <span className="text-sm text-[#475569] flex items-center gap-2">
 <ArrowUpCircle className="w-4 h-4 text-green-600"/>
 Incoming
 </span>
 <span className="font-bold text-[#0F172A]">{formatCurrency(forecast.forecast_30_days.incoming)}</span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm text-[#475569] flex items-center gap-2">
 <ArrowDownCircle className="w-4 h-4 text-red-600"/>
 Outgoing
 </span>
 <span className="font-bold text-[#0F172A]">{formatCurrency(forecast.forecast_30_days.outgoing)}</span>
 </div>
 </div>
 
 <div className={`p-4 rounded-lg border-2 ${getNetFlowColor(forecast.forecast_30_days.net)}`}>
 <p className="text-xs font-semibold mb-1">Net Cash Flow</p>
 <p className="text-2xl font-bold flex items-center gap-2">
 {forecast.forecast_30_days.net > 0 ? (
 <TrendingUp className="w-5 h-5"/>
 ) : (
 <TrendingDown className="w-5 h-5"/>
 )}
 {forecast.forecast_30_days.net >= 0 ? '+' : ''}{formatCurrency(forecast.forecast_30_days.net)}
 </p>
 </div>

 <div className="pt-3 border-t flex items-center justify-between">
 <div>
 <p className="text-xs text-[#475569] mb-1">Projected Position</p>
 <p className={`text-xl font-bold ${getCashPositionColor(forecast.forecast_30_days.projected_position)}`}>
 {formatCurrency(forecast.forecast_30_days.projected_position)}
 </p>
 </div>
 <ChevronRight className="w-5 h-5 text-slate-400"/>
 </div>
 </CardContent>
 </Card>

 {/* 60 Days */}
 <Card 
 className="border-blue-200/60 hover:shadow-lg transition-all cursor-pointer"
 onClick={() => setShowForecast60Modal(true)}
 data-testid="forecast-60-card"
 >
 <CardHeader className="border-b border-white/40 flex flex-row items-center justify-between">
 <CardTitle className="flex items-center gap-2 text-[#0F172A]">
 <Calendar className="w-5 h-5"/>
 31-60 Days
 </CardTitle>
 <Badge variant="outline"className="bg-blue-500/10 text-blue-700">
 {(forecast.forecast_60_days.incoming_transactions?.length || 0) + (forecast.forecast_60_days.outgoing_transactions?.length || 0)} items
 </Badge>
 </CardHeader>
 <CardContent className="p-6 space-y-4">
 <div className="space-y-3">
 <div className="flex justify-between items-center">
 <span className="text-sm text-[#475569] flex items-center gap-2">
 <ArrowUpCircle className="w-4 h-4 text-green-600"/>
 Cumulative In
 </span>
 <span className="font-bold text-green-700">{formatCurrency(forecast.forecast_60_days.incoming)}</span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm text-[#475569] flex items-center gap-2">
 <ArrowDownCircle className="w-4 h-4 text-red-600"/>
 Cumulative Out
 </span>
 <span className="font-bold text-red-700">{formatCurrency(forecast.forecast_60_days.outgoing)}</span>
 </div>
 </div>
 
 <div className={`p-4 rounded-lg border-2 ${getNetFlowColor(forecast.forecast_60_days.net)}`}>
 <p className="text-xs font-semibold mb-1">Net Cash Flow</p>
 <p className="text-2xl font-bold flex items-center gap-2">
 {forecast.forecast_60_days.net > 0 ? (
 <TrendingUp className="w-5 h-5"/>
 ) : (
 <TrendingDown className="w-5 h-5"/>
 )}
 {forecast.forecast_60_days.net >= 0 ? '+' : ''}{formatCurrency(forecast.forecast_60_days.net)}
 </p>
 </div>

 <div className="pt-3 border-t flex items-center justify-between">
 <div>
 <p className="text-xs text-[#475569] mb-1">Projected Position</p>
 <p className={`text-xl font-bold ${getCashPositionColor(forecast.forecast_60_days.projected_position)}`}>
 {formatCurrency(forecast.forecast_60_days.projected_position)}
 </p>
 </div>
 <ChevronRight className="w-5 h-5 text-slate-400"/>
 </div>
 </CardContent>
 </Card>

 {/* 90 Days */}
 <Card 
 className="border-orange-200/60 hover:shadow-lg transition-all cursor-pointer"
 onClick={() => setShowForecast90Modal(true)}
 data-testid="forecast-90-card"
 >
 <CardHeader className="border-b border-white/40 flex flex-row items-center justify-between">
 <CardTitle className="flex items-center gap-2 text-[#0F172A]">
 <Calendar className="w-5 h-5"/>
 61-90 Days
 </CardTitle>
 <Badge variant="outline"className="bg-amber-500/10 text-amber-700">
 {(forecast.forecast_90_days.incoming_transactions?.length || 0) + (forecast.forecast_90_days.outgoing_transactions?.length || 0)} items
 </Badge>
 </CardHeader>
 <CardContent className="p-6 space-y-4">
 <div className="space-y-3">
 <div className="flex justify-between items-center">
 <span className="text-sm text-[#475569] flex items-center gap-2">
 <ArrowUpCircle className="w-4 h-4 text-green-600"/>
 Cumulative In
 </span>
 <span className="font-bold text-green-700">{formatCurrency(forecast.forecast_90_days.incoming)}</span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm text-[#475569] flex items-center gap-2">
 <ArrowDownCircle className="w-4 h-4 text-red-600"/>
 Cumulative Out
 </span>
 <span className="font-bold text-red-700">{formatCurrency(forecast.forecast_90_days.outgoing)}</span>
 </div>
 </div>
 
 <div className={`p-4 rounded-lg border-2 ${getNetFlowColor(forecast.forecast_90_days.net)}`}>
 <p className="text-xs font-semibold mb-1">Net Cash Flow</p>
 <p className="text-2xl font-bold flex items-center gap-2">
 {forecast.forecast_90_days.net > 0 ? (
 <TrendingUp className="w-5 h-5"/>
 ) : (
 <TrendingDown className="w-5 h-5"/>
 )}
 {forecast.forecast_90_days.net >= 0 ? '+' : ''}{formatCurrency(forecast.forecast_90_days.net)}
 </p>
 </div>

 <div className="pt-3 border-t flex items-center justify-between">
 <div>
 <p className="text-xs text-[#475569] mb-1">Projected Position</p>
 <p className={`text-xl font-bold ${getCashPositionColor(forecast.forecast_90_days.projected_position)}`}>
 {formatCurrency(forecast.forecast_90_days.projected_position)}
 </p>
 </div>
 <ChevronRight className="w-5 h-5 text-slate-400"/>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Data Sources Info */}
 <Card className="bg-white/50">
 <CardContent className="pt-6">
 <p className="text-sm font-semibold text-[#334155] mb-2">Data Sources</p>
 <div className="grid grid-cols-4 gap-4 text-xs text-[#475569]">
 <div>
 <p className="font-semibold text-[#334155]">{forecast.data_sources.reservations_count}</p>
 <p>Confirmed Sales</p>
 </div>
 <div>
 <p className="font-semibold text-[#334155]">{forecast.data_sources.pending_invoices_count}</p>
 <p>Pending Invoices</p>
 </div>
 <div>
 <p className="font-semibold text-[#334155]">{forecast.data_sources.actual_costs_count}</p>
 <p>Actual Costs</p>
 </div>
 <div>
 <p className="font-semibold text-[#334155]">{forecast.data_sources.active_rentals_count}</p>
 <p>Active Rentals</p>
 </div>
 </div>
 <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/60 text-xs">
 <div>
 <p className="text-[#94A3B8]">Total Expected Revenue</p>
 <p className="font-semibold text-[#334155]">{formatCurrency(forecast.summary.total_expected_revenue)}</p>
 </div>
 <div>
 <p className="text-[#94A3B8]">Revenue Secured</p>
 <p className="font-semibold text-green-600">{forecast.summary.revenue_secured_pct.toFixed(1)}%</p>
 </div>
 <div>
 <p className="text-[#94A3B8]">Monthly Rental Income</p>
 <p className="font-semibold text-blue-600">{formatCurrency(forecast.summary.monthly_rental_income)}</p>
 </div>
 </div>
 <p className="text-[11px] text-[#94A3B8] mt-3 italic">
 * Forecast based on confirmed sales payment schedules, outstanding partner invoices, and active rentals
 </p>
 </CardContent>
 </Card>
 </>
 ) : (
 <div className="text-center py-12 text-[#94A3B8]">
 No forecast data available
 </div>
 )}
 </div>

 {/* Modals */}
 <CurrentPositionModal 
 open={showCurrentPositionModal} 
 onClose={setShowCurrentPositionModal}
 currentPosition={forecast?.current_position}
 />
 <ForecastPeriodModal 
 open={showForecast30Modal} 
 onClose={setShowForecast30Modal}
 title="Next 30 Days Forecast"
 period="the next 30 days"
 data={forecast?.forecast_30_days}
 />
 <ForecastPeriodModal 
 open={showForecast60Modal} 
 onClose={setShowForecast60Modal}
 title="31-60 Days Forecast"
 period="days 31-60"
 data={forecast?.forecast_60_days}
 />
 <ForecastPeriodModal 
 open={showForecast90Modal} 
 onClose={setShowForecast90Modal}
 title="61-90 Days Forecast"
 period="days 61-90"
 data={forecast?.forecast_90_days}
 />
 <OutstandingInvoicesModal 
 open={showOutstandingInvoicesModal}
 onClose={setShowOutstandingInvoicesModal}
 outstandingInvoices={forecast?.outstanding_invoices}
 />
 </Layout>
 );
};

export default CashFlowPageV2;
